<template>
    <div id="payment-form">
        <div class="panel uk-container uk-padding">
            <h2>
                Edit payment
            </h2>
            <div id="payment-details">
                <form-wrapper
                    class="uk-form-stacked"
                    :validator="$v.buffer.payment"
                    :messages="messagesOverride"
                >
                    <div
                        uk-grid
                        class="uk-grid-small uk-margin-bottom"
                    >
                        <select-field
                            v-model="buffer.payment.paymentStatus"
                            placeholder="Payment Status"
                            name="paymentStatus"
                            :options="sortedPaymentStatuses"
                            :v="$v.buffer.payment.paymentStatus"
                            :disabled="!canEditPaymentStatus"
                            label="Payment Status"
                            class="uk-width-1-2@l"
                            :show-error-on-blur-only="true"
                            @input="updatePayment"
                        />
                    </div>
                    <div
                        uk-grid
                        class="uk-grid-small uk-margin-bottom@l"
                    >
                        <text-field
                            v-model="buffer.payment.jobNumber"
                            placeholder=""
                            name="jobNumber"
                            label="Job Number"
                            :v="$v.buffer.payment.jobNumber"
                            :disabled="true"
                            class="uk-width-1-2@s uk-width-1-4@l"
                            :max-length="5"
                            :show-error-on-blur-only="true"
                            @input="updatePayment"
                        />
                        <select-field
                            v-model="buffer.payment.paymentState"
                            name="paymentState"
                            :options="sortedPaymentStates"
                            :v="$v.buffer.payment.paymentState"
                            :disabled="!canEditPaymentStatus"
                            label="State"
                            class="uk-width-1-4@s"
                            :show-error-on-blur-only="true"
                            @input="updatePayment"
                        />
                    </div>
                    <div
                        uk-grid
                        class="uk-grid-small"
                    >
                        <div class="uk-width-1-2@s uk-width-1-4@l">
                            <select-field
                                v-model="buffer.payment.receiptTypeID"
                                placeholder="Receipt Type"
                                name="receiptTypeID"
                                :options="sortedReceiptTypes"
                                :v="$v.buffer.payment.receiptTypeID"
                                :disabled="!canEditPaymentStatus"
                                label="Receipt Type"
                                :show-error-on-blur-only="true"
                                @input="updatePayment"
                            />
                        </div>
                    </div>
                    <div
                        v-if="payment.receiptTypeID"
                        uk-grid
                        class="uk-grid-small"
                    >
                        <div class="uk-width-1-2@s uk-width-1-4@l">
                            <select-field
                                v-model="buffer.payment.paymentType"
                                placeholder="Payment Type"
                                name="paymentType"
                                :options="sortedPaymentTypes"
                                :v="$v.buffer.payment.paymentType"
                                :disabled="!canEditPaymentStatus"
                                label="Payment Type"
                                :show-error-on-blur-only="true"
                                @input="updatePayment"
                            />
                        </div>
                        <div class="uk-width-1-2@s uk-width-1-4@l">
                            <select-field
                                v-model="buffer.payment.paymentMethod"
                                placeholder="Payment Method"
                                name="paymentMethod"
                                :options="paymentMethods"
                                :v="$v.buffer.payment.paymentMethod"
                                :disabled="true"
                                label="Payment Method"
                                :show-error-on-blur-only="true"
                                @input="updatePayment"
                            />
                            <small class="uk-text-muted">
                                Payment method cannot be updated.
                            </small>
                        </div>
                    </div>
                    <div
                        v-if="payment.paymentState && payment.paymentType && payment.paymentMethod"
                        uk-grid
                        class="uk-grid-small"
                    >
                        <div class="uk-width-1-2@s">
                            <formatted-numeric-field
                                v-model="buffer.payment.amount"
                                placeholder="0.00"
                                name="amount"
                                label="Amount"
                                :v="$v.buffer.payment.amount"
                                :disabled="!canEditPaymentStatus"
                                :options="amountFieldOptions"
                                :show-error-on-blur-only="true"
                                @input="updatePayment"
                            />
                            <!-- SIC-262: Simonds will absorb surcharge
                            <div
                                v-if="isCreditCardPayment"
                                class="uk-margin-small-top"
                            >
                                <small class="uk-text-muted">
                                    {{ amountNote }}
                                </small>
                            </div>
                            -->
                        </div>
                    </div>
                    <div
                        v-if="payment.paymentType && payment.paymentMethod"
                        uk-grid
                        class="uk-grid-small"
                    >
                        <text-field
                            v-model="buffer.payment.notes"
                            name="notes"
                            label="Description"
                            :v="$v.buffer.payment.notes"
                            :disabled="!canEditPaymentStatus"
                            class="uk-width-1-2@l"
                            :show-error-on-blur-only="true"
                            @input="updatePayment"
                        />
                    </div>
                </form-wrapper>
                <credit-card-payment-form
                    v-if="isCreditCardPayment"
                    mode="edit"
                    :cc="cc"
                    :enable-validation="isCreditCardPayment"
                    @change="onCreditCardPaymentFormChange"
                />
                <cheque-payment-form
                    v-if="isChequePayment"
                    mode="edit"
                    :cheque="cheque"
                    :enable-validation="isChequePayment"
                    @change="onChequePaymentFormChange"
                />
            </div>
            <div
                id="customer-details"
                uk-grid
                class="uk-margin-large-top"
            >
                <div class="uk-width-1-2@l">
                    <div>
                        <header
                            uk-grid
                            class="user-details-header uk-margin-bottom"
                        >
                            <h3 class="uk-width-1-2@s">
                                Customer details
                            </h3>
                        </header>
                        <user-details-form
                            :key="userDetailsFormKey"
                            mode="edit"
                            :enable-validation="true"
                            :should-job-detail-be-retrieved="false"
                            :user-details="payer"
                            :is-user-detail-retrieved="true"
                            :states="states"
                            :titles="titles"
                            :is-credit-card-payment="isCreditCardPayment"
                            :show-is-primary="showIsPrimary"
                            @change="onUserFormChange"
                        />
                    </div>
                </div>
            </div>
            <div
                class="uk-margin-large-top uk-width-1-2@l uk-flex"
            >
                <div>
                    <span
                        style="font-size: 1.25rem; line-height: 1.4;"
                        class="uk-margin-large-right uk-text-middle"
                    >
                        Secondary User
                    </span>
                    <button
                        v-if="hasSecondaryUser || showSecondaryUser"
                        type="button"
                        class="uk-button uk-button-link add-customer-link"
                        @click="showClearSecondaryUserModal"
                    >
                        <div class="uk-inline uk-margin-small-right add-customer-icon">
                            <span class="uk-margin-small-right uk-margin-small-left" uk-icon="minus"/>
                        </div>
                    </button>
                    <button
                        v-else
                        type="button"
                        class="uk-button uk-button-link add-customer-link"
                        @click="showSecondaryUserForm"
                    >
                        <div class="uk-inline uk-margin-small-right add-customer-icon">
                            <span class="uk-margin-small-right uk-margin-small-left" uk-icon="plus"/>
                        </div>
                    </button>
                </div>
                <div
                    id="delete-secondary-user-modal"
                    uk-modal
                    bg-close="false"
                    esc-close="false"
                    container="deleteSecondaryUserModal"
                >
                    <div class="uk-modal-dialog uk-modal-body">
                        <h3 class="uk-modal-title">Delete Secondary User</h3>
                        <p>Are you sure you want to delete the secondary user?<br><span class="uk-text-small">Click 'Update' to save changes.</span></p>
                        <p class="uk-text-right">
                            <button class="uk-button uk-button-default" type="button" @click="closeDeleteSecondaryModal">Cancel</button>
                            <button class="uk-button uk-button-primary uk-margin-small-left" type="button" @click="clearSecondaryUser">Yes</button>
                        </p>
                    </div>
                </div>
            </div>
            <div v-show="hasSecondaryUser || showSecondaryUser" uk-grid class="uk-margin-top uk-width-1-2@m uk-width-1-2@l">
                <form-wrapper
                    class="uk-form-stacked"
                    :validator="$v.buffer.payment"
                >
                    <div uk-grid class="uk-margin-remove-top">
                        <text-field
                            v-model="buffer.payment.secondaryUserEmail"
                            placeholder="Email"
                            label="Email"
                            :hide-label="false"
                            name="secondary_user_email"
                            class="uk-width-1-1 uk-margin-small-top"
                            :v="$v.buffer.payment.secondaryUserEmail"
                            :show-error-on-blur-only="true"
                            @input="updatePayment"
                        />
                        <div uk-grid class="uk-grid-small uk-margin-small-top uk-margin-remove-bottom uk-width-1-1">
                            <div class="uk-width-1-2@l">
                                <text-field
                                    v-model="buffer.payment.secondaryUserFirstName"
                                    placeholder="First Name"
                                    label="First Name"
                                    :hide-label="false"
                                    name="secondary_user_firstname"
                                    :v="$v.buffer.payment.secondaryUserFirstName"
                                    :show-error-on-blur-only="true"
                                    @input="updatePayment"
                                />
                            </div>
                            <div class="uk-width-1-2@l">
                                <text-field
                                    v-model="buffer.payment.secondaryUserSurname"
                                    placeholder="Surname"
                                    label="Surname"
                                    :hide-label="false"
                                    name="secondary_user_surname"
                                    :v="$v.buffer.payment.secondaryUserSurname"
                                    :show-error-on-blur-only="true"
                                    @input="updatePayment"
                                />
                            </div>
                        </div>
                        <text-field
                            v-model="buffer.payment.secondaryUserMobileNumber"
                            placeholder="Contact Number"
                            label="Contact Number"
                            :hide-label="false"
                            name="secondary_user_mobile_number"
                            class="uk-width-1-1 uk-margin-small-top"
                            :v="$v.buffer.payment.secondaryUserMobileNumber"
                            :show-error-on-blur-only="true"
                            @input="updatePayment"
                        />
                    </div>
                </form-wrapper>
            </div>

            <system-message
                :success="success"
                :message="message"
                @close="clearSystemMessage"
            />
            <div
                class="api-node uk-margin-large-top"
                uk-grid
            >
                <div class="uk-width-1-1@m">
                    <checkbox-field
                        v-if="paymentDetailHasChanged"
                        v-model="buffer.notifyClient"
                        label="Send a new receipt to the customer"
                        name="notifyClient"
                        :show-error-on-blur-only="true"
                    />
                </div>
                <div class="uk-width-1-2@m uk-flex uk-flex-between">
                    <button
                        type="button"
                        class="uk-button uk-button-primary uk-margin-right"
                        :disabled="!isFormValid"
                        @click="updateDetails"
                    >
                        Update
                    </button>
                    <div class="uk-margin-auto-left">
                        <button
                            v-if="isUserAdmin"
                            type="button"
                            class="uk-button uk-button-danger uk-margin-small-left"
                            @click="showDeleteModal"
                        >
                            Delete
                        </button>
                    </div>
                </div>
                <div class="uk-width-1-1@m">
                    <button
                        type="button"
                        class="uk-button uk-button-link"
                        @click="goToIndex()"
                    >
                        Cancel
                    </button>
                </div>
            </div>
            <payment-attachment-form
                :id="id"
                :attachments="payment.attachments"
                @reload="reload"
                @showDeleteAttachmentModal="showDeleteAttachmentModal"
            />
            <div
                id="delete-attachment-modal"
                uk-modal
                bg-close="false"
                esc-close="false"
                container="deleteAttachmentModal"
            >
                <div
                    class="uk-modal-dialog uk-modal-body"
                >
                    <h3 class="uk-modal-title">Delete Attachment</h3>
                    <p>Are you sure you want to delete <b>{{ fileTitle }}</b>?</p>
                    <p class="uk-text-right">
                        <button class="uk-button uk-button-default" type="button" @click="closeDeleteAttachmentModal">Cancel</button>
                        <button class="uk-button uk-button-primary uk-margin-small-left" type="button" @click="deleteAttachment">Yes</button>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { email, required, minLength, maxLength } from "vuelidate/lib/validators";
import {
    getPaymentFormDropdownItemsRequest,
    updatePaymentRequest,
    deletePaymentRequest,
    deletePaymentAttachmentRequest
} from '../../helpers/api'
import { copyData, sortByKey, cloneObject, isSimondsEmail } from '@strangeanimals/common-vue-components/src/helpers/helpers';
import router from '../../helpers/router'
import { PaymentMethods, PaymentTypes, ReceiptTypes } from '../../helpers/constants'
import PaymentAttachmentForm from "./PaymentAttachmentForm";

const CheckboxField = () => import("@strangeanimals/common-vue-components/src/components/fields/CheckboxField");
const NumericField = () => import("@strangeanimals/common-vue-components/src/components/fields/NumericField");
const TextField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/TextField");
const SelectField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/SelectField");
const ChequePaymentForm  = () => import( "./ChequePaymentForm");
const CreditCardPaymentForm  = () => import( "./CreditCardPaymentForm");
const UserDetailsForm  = () => import( "./UserDetailsForm");
const SystemMessage  = () => import( "@strangeanimals/common-vue-components/src/components/page/SystemMessage");
const FormattedNumericField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/FormattedNumericField");

const notSimondsEmail = (value, vm) => (!(isSimondsEmail(value)));

export default {
    // eslint-disable-next-line vue/no-unused-components
    components: { CheckboxField, FormattedNumericField, UserDetailsForm, CreditCardPaymentForm, ChequePaymentForm, TextField, NumericField, SelectField, SystemMessage, PaymentAttachmentForm },
    props: {
        id: {
            type: Number,
            default: () => {
                return null;
            }
        },
        payment: {
            type: Object,
            default: () => {
                return {
                    jobNumber: '',
                    paymentState: '',
                    paymentType: '',
                    paymentMethod: '',
                    paymentStatus: '',
                    receiptTypeID: '',
                    amount: '',
                    notes: '',
                    transaction: {},
                    secondaryUserFirstName: '',
                    secondaryUserSurname: '',
                    secondaryUserEmail: '',
                    secondaryUserMobileNumber: '',
                    attachments: [],
                };
            }
        },
        paymentUser: {
            type: Object,
            default: () => {
                return {};
            }
        },
    },
    data: function () {
        return {
            cc: {
                cardNumber: '',
                expiryMonth: '',
                expiryYear: '',
                cvn: '',
                cardholderName: '',
            },
            cheque: {
                bank: '',
                branch: '',
                drawer: '',
            },
            payer: {
                user: {
                    title: '',
                    firstName: '',
                    surname: '',
                    mobileNumber: '',
                    email: '',
                },
                address: {
                    address1: '',
                    address2: '',
                    suburb: '',
                    state: '',
                    postcode: '',
                },
                isPrimary: false
            },
            titles: {},
            paymentTypes: {},
            paymentStatuses: {},
            paymentStates: {},
            paymentMethods: {},
            surchargeForStates: {},
            receiptTypes: {},
            states: {
                VIC: 'VIC',
                WA: 'WA',
                NSW: 'NSW',
                TAS: 'TAS',
                NT: 'NT',
                QLD: 'QLD',
                SA: 'SA'
            },
            success: true,
            message: [],
            isChequePaymentFormValid: false,
            isCreditCardPaymentFormValid: false,
            isUserFormValid: false,
            messagesOverride: {
                minLength: "Field {attribute} should be 5 characters.",
                maxLength: "Field {attribute} should be 5 characters."
            },
            amountWithBpointSurcharge: null,
            amountFieldOptions: {
                prefix: '$ ',
                numeral: true,
                numeralPositiveOnly: true,
                noImmediatePrefix: true,
                rawValueTrimPrefix: true,
                numeralIntegerScale: 9,
                numeralDecimalScale: 2
            },
            buildUsers: [],
            userDetailsFormKey: 0,
            buffer: {
                payment: cloneObject(this.payment),
                notifyClient: false
            },
            initialPaymentData: {},
            paymentDetailHasChanged: false,
            showSecondaryUser: false,
            fileID: null,
            fileTitle: '',
        };
    },
    computed: {
        ...mapGetters({
            payments: 'payments',
            loggedInUser: 'user',
            isUserAdmin: 'isUserAdmin',
            defaultRequestParams: 'defaultRequestParams',
            isLoading: 'isLoading'
        }),
        isFormValid: function () {
            return !this.$v.buffer.payment.$invalid
                && this.isUserFormValid
                && (!this.isCreditCardPayment || this.isCreditCardPaymentFormValid)
                && (!this.isChequePayment || this.isChequePaymentFormValid);
        },
        sortedPaymentTypes: function () {
            if (!this.isCreditCardPayment) {
                let paymentTypes = cloneObject(this.paymentTypes);
                delete paymentTypes[PaymentTypes.TENDER_FEE];
                return sortByKey(paymentTypes);
            }

            return sortByKey(this.paymentTypes)
        },
        sortedPaymentStatuses: function () {
            return sortByKey(this.paymentStatuses)
        },
        sortedPaymentStates: function () {
            return sortByKey(this.paymentStates)
        },
        sortedReceiptTypes: function () {
            // This is done so that the receiptType value will be shown even if the user does not have the receipt type
            let result = {};
            result[this.payment.receiptTypeID] = this.receiptTypes[this.payment.receiptTypeID];
            return result;
        },
        isCashPayment: function () {
            return this.payment.paymentMethod === PaymentMethods.CASH;
        },
        isCreditCardPayment: function () {
            return this.payment.paymentMethod === PaymentMethods.CREDIT_CARD;
        },
        isEftPayment: function () {
            return this.payment.paymentMethod === PaymentMethods.EFT;
        },
        isChequePayment: function () {
            return this.payment.paymentMethod === PaymentMethods.CHEQUE;
        },
        isPaymentTypeOther: function () {
            return this.payment.paymentType === PaymentTypes.OTHER;
        },
        isPreliminaryPaymentType: function () {
            return ((this.payment.paymentType === PaymentTypes.INITIAL_PRELIMINARY_FEE) || (this.payment.paymentType === PaymentTypes.FURTHER_PRELIMINARY_FEE));
        },
        isMasterpiecePayment: function () {
            return this.receiptTypes[this.payment.receiptTypeID] === ReceiptTypes.MASTERPIECE;
        },
        payload: function () {
            return {
                ...this.defaultRequestParams,
                ...this.objectDetails,
                ...this.paymentDetails,
                payload: this.userDetails,
                transaction: this.transactionDetails,
                notify_client: this.buffer.notifyClient
            }
        },
        paymentDetails: function () {
            return {
                status: this.buffer.payment.paymentStatus,
                amount: this.buffer.payment.amount,
                notes: this.buffer.payment.notes,
                receipt_type_id: this.buffer.payment.receiptTypeID,
                build_region_state_id: this.buffer.payment.paymentState,
                secondary_user_firstname: this.buffer.payment.secondaryUserFirstName,
                secondary_user_surname: this.buffer.payment.secondaryUserSurname,
                secondary_user_email: this.buffer.payment.secondaryUserEmail,
                secondary_user_mobile_number: this.buffer.payment.secondaryUserMobileNumber,
            };
        },
        objectDetails: function () {
            return {
                payment_type: this.buffer.payment.paymentType,
                payment_method: this.buffer.payment.paymentMethod,
            }
        },
        chequeDetails: function () {
            return {
                bank: this.cheque.bank,
                branch: this.cheque.branch,
                drawer: this.cheque.drawer,
            }
        },
        transactionDetails: function () {
            if (this.isChequePayment) {
                return this.chequeDetails;
            } else {
                return {};
            }
        },
        userDetails: function () {
            return {
                user: {
                    title: this.payer.user.title,
                    first_name: this.payer.user.firstName,
                    surname: this.payer.user.surname,
                    mobile_number: this.payer.user.mobileNumber,
                    email: this.payer.user.email,
                },
                address: {
                    address_1: this.payer.address.address1,
                    address_2: this.payer.address.address2,
                    suburb: this.payer.address.suburb,
                    state: this.payer.address.state,
                    postcode: this.payer.address.postcode
                },
                isPrimary: this.payer.isPrimary
            };
        },
        canEditPaymentStatus() {
            return this.isUserAdmin;
        },
        surchargePercentage() {
            return parseFloat(this.surchargeForStates[this.payment.paymentState]);
        },
        /** SIC-262 Simonds will absorb surcharge
        amountNote() {
            let note = "There is a " + this.surchargePercentage.toString() + "% fee for credit card transactions."
            if (this.amountWithBpointSurcharge) {
                note += "The customer will be charged $" + this.amountWithBpointSurcharge + ".";
            }
            return note;
        },
         */
        isUserAlreadyExisting() {
            return this.buildUsers.length > 0;
        },
        showIsPrimary() {
            const payerEmail = this.payer.user.email;
            const isPayerEmailAlreadyIncludedInBuildUsers = this.buildUsers.find((user) => (user.user.email ===  payerEmail)) !== undefined;
            return (this.buildUsers.length > 1) || ((this.buildUsers.length === 1) && !isPayerEmailAlreadyIncludedInBuildUsers);
        },
        hasOnlyOneReceiptTypeOption() {
            return Object.keys(this.receiptTypes).length === 1;
        },
        billerCodeSelector() {
            let result = 'payments-app';
            // ZOSIM-106: Initial or Further preliminary payments with amount <= 3000 will have no surcharge.
            if (this.isPreliminaryPaymentType && this.payment.amount.length > 0 && this.payment.amount <= 3000) {
                result = 'payments-app-no-fee';
            }
            return result;
        },
        hasSecondaryUser: function () {
            return this.buffer.payment.secondaryUserFirstName || this.buffer.payment.secondaryUserSurname
                || this.buffer.payment.secondaryUserMobileNumber || this.buffer.payment.secondaryUserEmail;
        },
    },
    watch: {
        payment: function () {
            this.buffer.payment = cloneObject(this.payment);

            if (this.isChequePayment) {
                this.cheque = this.payment.transaction
            }

            if (Object.keys(this.initialPaymentData).length === 0)
            {
                this.initialPaymentData = cloneObject(this.payment);
            }

            // LIMITATION: This part isn't working properly when select field is changed and then reverted.
            // Select field values become string, e.g., 1 => "1", thus, even if user reverted the change,
            // initial data and current data will still be different and the checkbox will still be displayed.
            this.paymentDetailHasChanged = JSON.stringify(this.initialPaymentData) !== JSON.stringify(this.buffer.payment);
            if (!this.paymentDetailHasChanged)
            {
                this.buffer.notifyClient = false;
            }
        },
        paymentUser: {
            handler() {
                copyData(this.paymentUser.user, this.payer.user);
                copyData(this.paymentUser.address, this.payer.address);
            },
            deep: true
        }
    },
    mounted() {
        this.getPaymentFormDropdownItems();
    },
    validations() {
        return {
            buffer: {
                payment: {
                    jobNumber: { required, minLength: minLength(5), maxLength: maxLength(5) },
                    paymentState: { required },
                    paymentType: { required },
                    paymentMethod: { required },
                    paymentStatus: { required },
                    receiptTypeID: { required },
                    amount: { required },
                    notes: this.isPaymentTypeOther ? { required } : {},
                    secondaryUserFirstName: {},
                    secondaryUserSurname:  {},
                    secondaryUserMobileNumber: {},
                    secondaryUserEmail: { email, notSimondsEmail }
                }
            }
        };
    },
    methods: {
        updateDetails() {
            const self = this;
            const data = {
                id: this.id,
                ...this.payload
            };
            updatePaymentRequest(data).then((response) => {
                if (response.body) {
                    self.setSystemMessage(["Payment details have been updated."], true);
                    this.$emit('reload', this.id);
                }
                else {
                    self.setSystemMessage(["There was an error when updating payment details."], false);
                }
            });
        },
        setSystemMessage(message, isSuccess) {
            this.message = message;
            this.success = isSuccess;
        },
        getPaymentFormDropdownItems() {
            const self = this;
            getPaymentFormDropdownItemsRequest({
                billerCodeSelector: self.billerCodeSelector
            }).then((response) => {
                self.paymentStatuses = response.body.paymentStatuses;
                self.paymentTypes = response.body.paymentTypes;
                self.paymentStates = response.body.paymentStates;
                self.paymentMethods = response.body.paymentMethods;
                self.titles = response.body.titles;
                self.surchargeForStates = response.body.surchargeForStates;
                self.receiptTypes = response.body.receiptTypes;
                // When there's only one in the dropdown, and the value is not set yet, set the lone dropdown value as default
                if (!self.payment.receiptTypeID && self.hasOnlyOneReceiptTypeOption) {
                    self.buffer.payment.receiptTypeID = Object.keys(self.receiptTypes)[0];
                }
            });
        },
        goToIndex() {
            router.push({
                name: 'Dashboard',
            });
        },
        onChequePaymentFormChange(data) {
            this.cheque = data.cheque;
            this.isChequePaymentFormValid = data.isValid;
        },
        onCreditCardPaymentFormChange(data) {
            this.cc = data.cc;
            this.isCreditCardPaymentFormValid = data.isValid;
        },
        onUserFormChange(data) {
            this.payer = data.userDetails;
            this.isUserFormValid = data.isValid;
        },
        showDeleteModal() {
            const self = this;
            this.$uikit.modal.confirm('Are you sure you wish to delete this payment?').then(function()  {
                self.deletePayment();
            });
        },
        deletePayment() {
            const data = { paymentID: this.id };

            deletePaymentRequest(data).then((response) => {
                if (response.status === 200 && response.body.deleted) {
                    this.goToIndex();
                }
                else {
                    this.setSystemMessage(["Failed to delete payment."], false);
                }
            });

        },
        clearSystemMessage() {
            this.message = [];
        },
        updatePayment() {
            return this.$emit('change', { payment: this.buffer.payment });
        },
        showSecondaryUserForm() {
            this.showSecondaryUser = true;
        },
        closeDeleteSecondaryModal() {
            this.$uikit.modal('#delete-secondary-user-modal').hide();
        },
        showClearSecondaryUserModal() {
            if (this.hasSecondaryUser) {
                this.$uikit.modal('#delete-secondary-user-modal').show();
            } else {
                this.clearSecondaryUser();
            }
        },
        clearSecondaryUser() {
            this.$uikit.modal('#delete-secondary-user-modal').hide();
            this.showSecondaryUser = false;
            this.buffer.payment.secondaryUserFirstName = '';
            this.buffer.payment.secondaryUserSurname = '';
            this.buffer.payment.secondaryUserEmail = '';
            this.buffer.payment.secondaryUserMobileNumber = '';
        },
        deleteAttachment() {
            const self = this;
            deletePaymentAttachmentRequest(self.fileID).then((response) => {
                self.closeDeleteAttachmentModal();
                if (response.body) {
                    self.$emit('reload', this.id);
                    this.setSystemMessage(['File deleted.'], true);
                }
                else {
                    this.setSystemMessage(['File delete failed.'], false);
                }
            });
        },
        showDeleteAttachmentModal(file) {
            this.fileID = file.id;
            this.fileTitle = file.title;
            this.$uikit.modal('#delete-attachment-modal').show();
        },
        closeDeleteAttachmentModal() {
            this.fileID = null;
            this.fileTitle = '';
            this.$uikit.modal('#delete-attachment-modal').hide();
        },
        reload() {
            this.$emit('reload', this.id);
        }
    },
}
</script>

<style>
    .add-customer-icon {
        border: 1px solid #1e87f0;
        border-radius: 5px;
        font-weight: bold;
    }
</style>